/* eslint-disable react-hooks/exhaustive-deps */
import { ChartBarIcon, PlusCircleIcon } from "@heroicons/react/20/solid";
import {
  Button,
  Flex, Grid, LoadingOverlay, Paper, Select, Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import cn from "../../../../../utils/styleHelpers";
import { useMutation } from "@tanstack/react-query";
import { subDays } from "date-fns";
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import uuid from "react-uuid";
import { queryClient, useTraitsAndMetrics } from "../../../../../hooks/QueryHooks";
import { getCompanyMetrics } from "../../../../../services/companyEndpoint";
import { createMetric, deleteMetric, updateMetric } from "../../../../../services/metric/metricEndpoint";
import { updateWorkspacePreference } from "../../../../../services/preference/preferenceEndpoints";
import dateConverter from "../../../../../utils/dateConverter";
import notify from "../../../../../utils/notify";
import CustomChart from "./CustomChart";
import NewTrendModal from "./NewTrendModal";

type Props = {
  account: any;
  prefs: any;
  metrics: any;
  currentWorkspace: any;
  containerClassName?: string;
};

const TrendsContainer = ({
  account, currentWorkspace, prefs, metrics, containerClassName,
}: Props) => {
  const [opened, { close, open }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);

  const updatePrefMutation: any = useMutation({
    mutationFn: updateWorkspacePreference,
    onSuccess: () => {
      queryClient.invalidateQueries(["workspace_preferences", currentWorkspace.id]);
    },
  });

  const deleteMetricMutation: any = useMutation({
    mutationFn: deleteMetric,
  });

  const updateMetricMutation: any = useMutation({
    mutationFn: updateMetric,
  });

  const firstRender = useRef(true);

  // Initialize state with values from preferences or defaults
  // Using the prefs directly in the initial state to avoid flashing of default values
  const [bucket, setBucket] = useState<any>(
    prefs?.data?.accounts?.metrics?.grouping || "day"
  );
  
  const [duration, setDuration] = useState<any>(
    prefs?.data?.accounts?.metrics?.time_window || "last_7_days"
  );
  
  // Effect to update state when preferences change
  // This ensures values are preserved when switching between views
  useEffect(() => {
    if (prefs?.data?.accounts?.metrics) {
      const prefBucket = prefs.data.accounts.metrics.grouping;
      const prefTimeWindow = prefs.data.accounts.metrics.time_window;
      
      if (prefBucket && prefBucket !== bucket) {
        setBucket(prefBucket);
      }
      
      if (prefTimeWindow && prefTimeWindow !== duration) {
        setDuration(prefTimeWindow);
      }
    }
  }, [prefs?.data?.accounts?.metrics, bucket, duration]);
  const [tableData, setTableData] = useState<any>({});

  useEffect(() => {
    if (bucket && duration) {
      if (duration !== prefs?.data?.accounts?.metrics?.time_window && !firstRender.current) {
        // Pass accounts and users at the top level as required by the API
        updatePrefMutation.mutate({
          workspace_id: currentWorkspace.id,
          accounts: {
            ...prefs?.data?.accounts,
            metrics: {
              grouping: bucket,
              time_window: duration,
              items: [],
            },
          },
          users: prefs?.data?.users, // Preserve existing users data
          updated_by: prefs?.data?.updated_by,
          id: prefs?.data?.id,
        });
      } else {
        firstRender.current = false;
      }
    }
  }, [bucket, duration]);

  const bucketList = useMemo(() => (
    [{ value: "day", label: "Daily" },
      { value: "week", label: "Weekly" },
      { value: "month", label: "Monthly" },
    ]
  ), []);

  const durationList = useMemo(() => {
    if (bucket === "day") {
      return [
        { value: "last_7_days", label: "Last 7 days" },
        { value: "last_30_days", label: "Last 30 days" },
        { value: "last_90_days", label: "Last 90 days" },
        { value: "last_180_days", label: "Last 180 days" },
      ];
    } if (bucket === "week") {
      return [
        { value: "last_4_weeks", label: "Last 4 weeks" },
        { value: "last_8_weeks", label: "Last 8 weeks" },
        { value: "last_12_weeks", label: "Last 12 weeks" },
        { value: "last_24_weeks", label: "Last 24 weeks" },
      ];
    } if (bucket === "month") {
      return [
        { value: "last_3_months", label: "Last 3 months" },
        { value: "last_6_months", label: "Last 6 months" },
        { value: "last_9_months", label: "Last 9 months" },
        { value: "last_12_months", label: "Last 12 months" },
      ];
    } return [];
  }, [bucket]);

  const onEditTitle = (id: any, title: any) => {
    updateMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
      display_name: title,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.map((item: any) => (item.id === data.data.id ? data.data : item)),
        }));
        notify({
          type: "success",
          messageList: ["Updated Pinned metric"],
        });
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Failed to update Pinned metric"],
        });
      },
    });
  };
  
  const onDeleteClick = (id: any) => {
    deleteMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.filter((item: any) => item.id !== data.data.id),
        }));
        if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "deleted_pinned_metric",
            {
              properties: {
                metric_id: id,
                workspace_id: currentWorkspace.id,
                entity_type: "accounts",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Removed Pinned metric"],
        });
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Failed to remove Pinned metric"],
        });
      },
    });
  };

  // Define getMetrics function outside of useEffect to make it available to other functions
  const getMetrics = async () => {
    setLoading(true);
    let days = 7;
    // Extract the numeric part from the time window string
    const durationMatch = duration.match(/\d+/);
    const durationNumber = durationMatch ? parseInt(durationMatch[0], 10) : 7;
    
    if (bucket === "day" || duration.includes('days')) {
      days = durationNumber;
    } else if (bucket === "week" || duration.includes('weeks')) {
      days = durationNumber * 7;
    } else if (bucket === "month" || duration.includes('months')) {
      days = durationNumber * 30;
    }
    try {
      const apiPromises = metrics?.map(async (event: any) => {
        const response = await getCompanyMetrics({
          bucket_size: bucket,
          external_id: account?.external_id,
          event_name: event.aggregator,
          workspace_identifier: currentWorkspace?.id,
          from_date: dateConverter(subDays(new Date(), days)),
        });
        return response;
      });
      const resolvedPromises = await Promise.all(apiPromises);
      resolvedPromises.forEach((response: any) => {
        if (response?.data) {
          setTableData((state: any) => ({ ...state, ...response.data }));
        }
      });
    } catch (error) {
      setTableData({});
    }
    setLoading(false);
  };
  
  useEffect(() => {
    if (!!account?.external_id && !!currentWorkspace?.id && metrics?.length > 0) {
      getMetrics();
    }
  }, [account?.external_id, bucket, duration, currentWorkspace?.id, metrics?.length]);

  const {
    data: metricData,
  }: any = useTraitsAndMetrics({
    workspaceId: currentWorkspace?.id,
    enabled: !!currentWorkspace?.id,
  });

  const selectedEvents = useMemo(() => metrics
    ?.map((item:any) => item.aggregator), [metrics]);

  const createMetricMutation: any = useMutation({
    mutationFn: createMetric,
  });

  const shownList = useMemo(() => (
    metricData?.accountMetrics?.list?.filter((item: any) => !selectedEvents?.find(
      (event: any) => event === item.name,
    ))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [selectedEvents, metricData?.accountMetrics?.length]);
  
  // Get the full list of metrics for the dropdown
  const fullMetricsList = useMemo(() => (
    metricData?.accountMetrics?.list || []
  ), [metricData?.accountMetrics?.list]);

  const onUpdateMetric = (id: any, metric: string, metricName: string, chartType: string = 'line') => {
    // Create a loading placeholder for the UI
    setTableData((prevData: Record<string, any>) => {
      const newData = { ...prevData };
      // Add a placeholder for the new metric to prevent UI from showing nothing
      newData[metric] = newData[metric] || {
        histogram_values: [],
        total_count: 0,
        percentage_change: 0
      };
      return newData;
    });
    
    // First delete the old metric
    deleteMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
    }, {
      onSuccess: () => {
        // Then create a new metric with the updated values
        createMetricMutation.mutate({
          workspace_id: currentWorkspace?.id,
          aggregator: metric,
          display_name: metricName,
          description: "",
          type: chartType, // Use the provided chart type
          id: `am_${metric}_${Math.random() * 1000}`,
          linked_entity: "accounts",
          is_default: false,
        }, {
          onSuccess: (newMetric: any) => {
            // Update the workspace metric preferences
            queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace?.id], (oldData: any) => {
              // Create a new array with the updated metrics
              const updatedData = {
                ...oldData,
                data: [...oldData.data.filter((item: any) => item.id !== id), newMetric.data],
              };
              return updatedData;
            });
            
            // Also update the metrics query data to ensure it's consistent
            queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace?.id, "accounts"], (oldData: any) => {
              if (!oldData) return oldData;
              // Create a new array with the updated metrics
              const updatedData = oldData.filter((item: any) => item.id !== id);
              updatedData.push(newMetric.data);
              return updatedData;
            });
            
            // Track the event if enabled
            if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
              window?.hyperengage(
                "track",
                "updated_pinned_metric",
                {
                  properties: {
                    display_name: metricName,
                    aggregator: metric,
                    workspace_id: currentWorkspace.id,
                    entity_type: "accounts",
                  },
                },
              );
            }
            
            // Show success notification
            notify({
              type: "success",
              messageList: ["Updated Pinned metric successfully"],
            });
            
            // Immediately fetch the new metric data
            getMetrics();
          },
          onFailure: () => {
            notify({
              type: "failure",
              messageList: ["Failed to update Pinned metric"],
            });
          },
        });
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Failed to update Pinned metric"],
        });
      },
    });
  };
  
  const onMetricSubmit = (metric: string, metricName: string, type: string) => {
    createMetricMutation.mutate({
      workspace_id: currentWorkspace?.id,
      aggregator: metric,
      display_name: metricName,
      description: "",
      type,
      id: `am_${metric}_${Math.random() * 1000}`,
      linked_entity: "accounts",
      is_default: false,
    }, {
      onSuccess: (newMetric: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace?.id], (oldData: any) => ({
          ...oldData,
          data: [...oldData.data, newMetric.data],
        }));
        if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "created_pinned_metric",
            {
              properties: {
                display_name: metricName,
                aggregator: metric,
                workspace_id: currentWorkspace.id,
                entity_type: "accounts",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Pinned metric successfully"],
        });
        close();
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Something went wrong"],
        });
      },
    });
  };

  return (
    <div className={cn("w-full bg-white border-gray-200 p-5", containerClassName)}>
      <NewTrendModal
        onMetricSubmit={onMetricSubmit}
        shownList={shownList}
        opened={opened}
        close={close}
      />
      <Flex justify="space-between" align="center" mb="md">
        <Text className="flex gap-2 items-center" fz={20} fw={600} c="text.9">
          <ChartBarIcon className="w-4 h-4 fill-purple-500" />
          Trends
        </Text>
        <Flex gap={10} align="center">
          <Select
            value={bucket}
            onChange={(value) => {
              if (!value) return;
              setBucket(value);
              let dur;
              if (value === "day") {
                dur = "last_7_days";
              } else if (value === "week") {
                dur = "last_4_weeks";
              } else if (value === "month") {
                dur = "last_3_months";
              }
              setDuration(dur);
            }}
            classNames={{
              input: "rounded-md w-[150px]",
              root: "focus:ring-purple-500",
            }}
            placeholder="Select range"
            data={bucketList}
            defaultValue="day"
          />
          <Select
            value={duration}
            onChange={(value) => {
              if (value) {
                setDuration(value);
                // Update the preference immediately when duration changes
                if (value !== prefs?.data?.accounts?.metrics?.time_window) {
                  updatePrefMutation.mutate({
                    workspace_id: currentWorkspace.id,
                    accounts: {
                      ...prefs?.data?.accounts,
                      metrics: {
                        grouping: bucket,
                        time_window: value,
                        items: [],
                      },
                    },
                    users: prefs?.data?.users,
                    updated_by: prefs?.data?.updated_by,
                    id: prefs?.data?.id,
                  });
                }
              }
            }}
            classNames={{
              input: "rounded-md w-[150px]",
              root: "focus:ring-purple-500",
            }}
            placeholder="Select range"
            data={durationList}
          />
          <div className="relative group">
            <Button 
              disabled={metrics?.length >= 8} 
              onClick={open} 
              className="text-sm" 
              color="white" 
              size="sm" 
              leftSection={<PlusCircleIcon className="w-5 h-5" />}
            >
              New pinned Metric
            </Button>
            {metrics?.length >= 8 && (
              <div className="absolute right-0 mt-1 w-60 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none text-center">
                Maximum pinned metrics reached. Remove some to add more.
              </div>
            )}
          </div>
        </Flex>
      </Flex>
      <div className="w-full mt-5">
        <Grid className="w-full min-h-[230px] relative">
          <LoadingOverlay loaderProps={{ size: "md" }} c="main.5" visible={loading} overlayProps={{ blur: 2 }} />
          {metrics?.length !== 0 ? (Object.keys(tableData)?.length !== 0
          && Object.entries(tableData).map(([key, data]: any) => {
            const selectedEvent = metrics?.find((event: any) => event.aggregator === key);
            return selectedEvent && (
              <Grid.Col key={uuid()} span={4}>
                <CustomChart
                  title={selectedEvent.display_name}
                  editTitleHandler={onEditTitle}
                  data={data}
                  type={selectedEvent.type}
                  deleteHandler={onDeleteClick}
                  id={selectedEvent.id}
                  updateMetricHandler={onUpdateMetric}
                  metricsList={fullMetricsList}
                  aggregator={selectedEvent.aggregator}
                />
              </Grid.Col>
            );
          })) : (
            <Paper p="xl" radius="md" className="bg-gray-50 italic text-gray-500 w-full flex items-center justify-center" shadow="sm">
              <Text size="sm" fw={500}>
                Add your first custom success metric and generate graphs to view trends.
              </Text>
            </Paper>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default TrendsContainer;
